import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Play, RefreshCw, Mail, MessageCircle, FileDown, CheckCircle2, XCircle, PauseCircle, PlayCircle, Send,
  AlertTriangle, Clock, Search,
} from "lucide-react";
import { toast } from "sonner";
import { LineSupplierDialog, isSupplierSelectionError } from "@/components/LineSupplierPicker";

// Automated MRP & Procurement (backend/auto_procurement.py): daily scheduled
// MRP across every store; Regular items → PR → PO → supplier email +
// WhatsApp + follow-ups; Non-Regular items → PR → Purchase Team.

const TABS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "runs", label: "Execution Log" },
  { key: "audit", label: "Audit Trail" },
  { key: "comms", label: "Communications" },
  { key: "settings", label: "Settings" },
];

const STATUS_TONE = {
  sent: "bg-emerald-50 text-emerald-700",
  delivered: "bg-emerald-50 text-emerald-700",
  read: "bg-emerald-50 text-emerald-700",
  received: "bg-blue-50 text-blue-700",
  queued: "bg-amber-50 text-amber-700",
  failed: "bg-red-50 text-red-700",
  not_configured: "bg-red-50 text-red-700",
  disabled: "bg-slate-100 text-slate-500",
  skipped: "bg-slate-100 text-slate-500",
  completed: "bg-emerald-50 text-emerald-700",
  completed_with_errors: "bg-amber-50 text-amber-700",
  running: "bg-blue-50 text-blue-700",
  ok: "bg-emerald-50 text-emerald-700",
  error: "bg-red-50 text-red-700",
  warning: "bg-amber-50 text-amber-700",
  pending: "bg-amber-50 text-amber-700",
  confirmed: "bg-emerald-50 text-emerald-700",
  declined: "bg-red-50 text-red-700",
};

const fmtDT = (v) => (v ? new Date(v).toLocaleString() : "—");
const fmtQty = (n) => (n == null ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: 3 }));

function Pill({ value, testid }) {
  if (!value) return <span className="text-slate-300">—</span>;
  return (
    <span data-testid={testid} className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_TONE[value] || "bg-slate-100 text-slate-600"}`}>
      {String(value).replace(/_/g, " ")}
    </span>
  );
}

function Kpi({ label, value, tone = "text-slate-900", testid }) {
  return (
    <div className="kpi-card" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className={`mt-2 text-2xl font-extrabold ${tone}`} style={{ fontFamily: "Manrope" }}>{value ?? "—"}</div>
    </div>
  );
}

function Section({ title, children, right }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
        <div className="text-sm font-semibold text-slate-800">{title}</div>
        {right}
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

const TH = "py-2.5 px-3 text-left text-[11px] uppercase tracking-[0.12em] text-slate-500 bg-slate-50 whitespace-nowrap";
const TD = "py-2.5 px-3 text-slate-700 align-top";

async function downloadPoPdf(po) {
  try {
    const { data } = await api.get(`/auto-procurement/purchase-orders/${po.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = `${po.code}.pdf`; document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  } catch {
    toast.error("PDF download failed");
  }
}

export function PoCommActions({ po, onDone, compact = false }) {
  const [busy, setBusy] = useState(false);
  // Lines still needing a supplier (409 from Send) + what to do once saved.
  const [selection, setSelection] = useState(null);
  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); onDone?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Action failed"); }
    finally { setBusy(false); }
  };
  const base = `/auto-procurement/purchase-orders/${po.id}`;
  // The dispatch endpoint answers 200 even when nothing reached the
  // supplier, so report each channel's real outcome instead of "sent".
  const dispatch = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/dispatch`);
      const label = (ch, st, to) => `${ch}: ${st === "sent" ? `sent${to ? ` to ${to}` : ""}` : st.replace(/_/g, " ")}`;
      // WhatsApp without an API account goes through "Share on WhatsApp",
      // so only mention it here when the API actually sent (or failed).
      const waApi = !["disabled", "use_share_button", "not_sent"].includes(data.whatsapp);
      const summary = label("Email", data.email, data.email_to)
        + (waApi ? ` · ${label("WhatsApp", data.whatsapp, data.whatsapp_to ? `+${data.whatsapp_to}` : "")}` : "");
      const errors = (data.errors || []).join("\n");
      if (data.delivered && !errors) toast.success(`${po.code} sent — ${summary}`);
      else if (data.delivered) toast.warning(`${po.code} partly sent — ${summary}`, { description: errors, duration: 12000 });
      else toast.error(`${po.code} was NOT delivered to the supplier — ${summary}`, { description: errors, duration: 15000 });
      // Internal copy to the signed-in user — reported on its own.
      const ie = data.internal_email;
      if (ie && ie.status === "sent") toast.success(`Internal notification emailed to ${ie.to}`);
      else if (ie) toast.error(`Internal notification email to ${ie.to || "you"} not delivered`, { description: ie.error || ie.status, duration: 12000 });
      onDone?.();
    } catch (e) {
      if (isSupplierSelectionError(e)) setSelection({ ...e.response.data.detail, then: "dispatch" });
      else toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally { setBusy(false); }
  };
  // Sends from the user's own WhatsApp (wa.me click-to-chat) — works
  // without a WhatsApp Business API account. The tab is opened before the
  // request so the browser doesn't treat it as a pop-up.
  const sendWhatsApp = async () => {
    const win = window.open("", "_blank");
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/whatsapp-link`);
      if (win) win.location.href = data.url; else window.location.href = data.url;
      toast.success(`WhatsApp opened for +${data.to} — press Send in WhatsApp`);
      onDone?.();
    } catch (e) {
      if (win) win.close();
      if (isSupplierSelectionError(e)) setSelection({ ...e.response.data.detail, then: "whatsapp" });
      else toast.error(formatApiError(e.response?.data?.detail) || "Could not open WhatsApp");
    } finally { setBusy(false); }
  };
  const onSelectionSaved = (data) => {
    const then = selection?.then;
    setSelection(null);
    toast.success(`Supplier saved on ${po.code}${data?.supplier ? ` — ${data.supplier}` : ""}`);
    if (then === "whatsapp") sendWhatsApp(); else dispatch();
  };
  const open = po.status === "open";
  const pending = !po.ack_status || po.ack_status === "pending";
  return (
    <div className={`flex flex-wrap gap-1 ${compact ? "" : "justify-end"}`}>
      <LineSupplierDialog selection={selection} onClose={() => setSelection(null)} onSaved={onSelectionSaved} />
      <Button size="sm" variant="outline" onClick={() => downloadPoPdf(po)} title="Download PO PDF" data-testid={`po-pdf-${po.code}`}>
        <FileDown className="w-3.5 h-3.5" />
      </Button>
      {open && (
        <Button size="sm" variant="outline" disabled={busy} title={po.dispatched_at ? "Re-send PO to supplier by email" : "Send PO to supplier by email"}
          onClick={() => window.confirm(`${po.dispatched_at ? "Re-send" : "Send"} ${po.code} to ${po.supplier || "the supplier"} by email? A copy goes to your email as an internal notification.`) && dispatch()}
          data-testid={`po-dispatch-${po.code}`}>
          <Send className="w-3.5 h-3.5" />
        </Button>
      )}
      {open && (
        <Button size="sm" disabled={busy} title="Opens WhatsApp with this supplier's chat and the PO details ready — press Send"
          className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap"
          onClick={sendWhatsApp} data-testid={`po-whatsapp-${po.code}`}>
          <MessageCircle className="w-3.5 h-3.5 mr-1" /> Share on WhatsApp
        </Button>
      )}
      {open && pending && (
        <>
          <Button size="sm" variant="outline" disabled={busy} title="Mark confirmed by supplier"
            onClick={() => act(() => api.post(`${base}/acknowledge`, { status: "confirmed", remarks: window.prompt("Remarks (optional)") || "" }), `${po.code} marked confirmed`)}
            data-testid={`po-confirm-${po.code}`}>
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
          </Button>
          <Button size="sm" variant="outline" disabled={busy} title="Mark declined by supplier"
            onClick={() => act(() => api.post(`${base}/acknowledge`, { status: "declined", remarks: window.prompt("Reason") || "" }), `${po.code} marked declined`)}
            data-testid={`po-decline-${po.code}`}>
            <XCircle className="w-3.5 h-3.5 text-red-600" />
          </Button>
        </>
      )}
      {open && pending && po.dispatched_at && (po.followup_active ? (
        <Button size="sm" variant="outline" disabled={busy} title="Stop automatic follow-ups"
          onClick={() => act(() => api.post(`${base}/stop-followups`, { reason: window.prompt("Reason for stopping follow-ups") || "" }), "Follow-ups stopped")}
          data-testid={`po-stop-fu-${po.code}`}>
          <PauseCircle className="w-3.5 h-3.5" />
        </Button>
      ) : (
        <Button size="sm" variant="outline" disabled={busy} title="Resume automatic follow-ups"
          onClick={() => act(() => api.post(`${base}/resume-followups`), "Follow-ups resumed")}
          data-testid={`po-resume-fu-${po.code}`}>
          <PlayCircle className="w-3.5 h-3.5" />
        </Button>
      ))}
    </div>
  );
}

function DashboardTab({ data, reload, canAct }) {
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;
  const c = data.counts || {};
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        <Kpi label="Items requiring procurement" value={c.items_requiring_procurement} testid="kpi-requiring" />
        <Kpi label="Auto PRs (today / total)" value={`${c.auto_prs_today ?? 0} / ${c.auto_prs_total ?? 0}`} testid="kpi-prs" />
        <Kpi label="Auto POs (today / total)" value={`${c.auto_pos_today ?? 0} / ${c.auto_pos_total ?? 0}`} testid="kpi-pos" />
        <Kpi label="POs sent to suppliers" value={c.pos_sent} testid="kpi-sent" />
        <Kpi label="Confirmations pending" value={c.confirmations_pending} tone="text-amber-700" testid="kpi-ack-pending" />
        <Kpi label="Follow-ups pending" value={c.followups_pending} tone="text-amber-700" testid="kpi-fu-pending" />
        <Kpi label="Non-Regular PRs — Purchase Team" value={c.purchase_team_pending} tone="text-indigo-700" testid="kpi-team" />
        <Kpi label="Failed email / WhatsApp" value={c.failed_notifications} tone={c.failed_notifications ? "text-red-700" : "text-slate-900"} testid="kpi-failed" />
        <Kpi label="Unclassified items below threshold" value={c.unclassified_requiring} tone={c.unclassified_requiring ? "text-amber-700" : "text-slate-900"} testid="kpi-unclassified" />
        <Kpi label="Last run" value={<Pill value={data.schedule?.last_status} />} testid="kpi-last-run" />
      </div>

      <Section title={`Items requiring procurement (${data.requiring?.length || 0})`}>
        <table className="w-full text-sm" data-testid="ap-requiring-table">
          <thead><tr>
            {["Item", "Class", "Stock (all stores)", "Reserved", "Open PR / PO / Transit", "BOM demand", "Projected", "Threshold", "MOQ", "Net req."].map((h) => <th key={h} className={TH}>{h}</th>)}
          </tr></thead>
          <tbody>
            {(data.requiring || []).length === 0 && <tr><td colSpan={10} className="py-8 text-center text-slate-500">No item is at or below its reorder threshold.</td></tr>}
            {(data.requiring || []).map((r) => (
              <tr key={r.item_id} className="border-t border-slate-100">
                <td className={TD}><div className="mono font-medium text-slate-800">{r.item_code}</div><div className="text-xs text-slate-500">{r.item_name}</div></td>
                <td className={TD}>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${r.procurement_class === "regular" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"}`}>
                    {r.procurement_class === "regular" ? "Regular" : "Non-Regular"}
                  </span>
                  {r.class_source && r.class_source !== "own" && <div className="text-[10px] text-slate-400 mt-0.5">via {r.class_source}</div>}
                </td>
                <td className={TD}>
                  <div className="mono">{fmtQty(r.physical_stock)} {r.uom}</div>
                  <div className="text-[10px] text-slate-500">{Object.entries(r.stores || {}).map(([k, v]) => `${k.replace("_STORE", "")}: ${fmtQty(v)}`).join(" · ") || "No stock"}</div>
                </td>
                <td className={`${TD} mono`}>{fmtQty(r.reserved_qty)}</td>
                <td className={`${TD} mono`}>{fmtQty(r.open_pr_qty)} / {fmtQty(r.open_po_qty)} / {fmtQty(r.in_transit_qty)}</td>
                <td className={`${TD} mono`}>{fmtQty(r.bom_demand_qty)}</td>
                <td className={`${TD} mono font-semibold text-red-700`}>{fmtQty(r.projected_qty)}</td>
                <td className={`${TD} mono`}>{fmtQty(r.threshold)}</td>
                <td className={`${TD} mono`}>{fmtQty(r.moq)}</td>
                <td className={`${TD} mono`}>{fmtQty(r.net_requirement)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section title={`Supplier confirmations pending (${data.pending_confirmations?.length || 0})`}>
        <table className="w-full text-sm" data-testid="ap-pending-ack-table">
          <thead><tr>{["PO", "Supplier", "Sent", "Email", "WhatsApp", "Follow-ups", "Next follow-up", ""].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
          <tbody>
            {(data.pending_confirmations || []).length === 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-500">No PO is waiting for supplier confirmation.</td></tr>}
            {(data.pending_confirmations || []).map((po) => (
              <tr key={po.id} className="border-t border-slate-100" data-testid={`ap-pending-${po.code}`}>
                <td className={TD}><div className="mono font-semibold">{po.code}</div><div className="text-[10px] text-slate-500">{po.pr_code}{po.auto_generated ? " · auto" : ""}</div></td>
                <td className={TD}>{po.supplier}</td>
                <td className={`${TD} text-xs`}>{fmtDT(po.dispatched_at)}</td>
                <td className={TD}><Pill value={po.email_status} /></td>
                <td className={TD}><Pill value={po.whatsapp_status} /></td>
                <td className={`${TD} mono`}>{po.followup_count || 0}{po.followup_active ? "" : <div className="text-[10px] text-slate-500">{po.followup_stop_reason || "stopped"}</div>}</td>
                <td className={`${TD} text-xs`}>{po.followup_active ? fmtDT(po.next_followup_at) : "—"}</td>
                <td className={TD}>{canAct && <PoCommActions po={po} onDone={reload} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section title={`Non-Regular PRs pending Purchase Team action (${data.purchase_team_prs?.length || 0})`}>
        <table className="w-full text-sm" data-testid="ap-team-table">
          <thead><tr>{["PR", "Class", "Lines", "Required by", "Reason", "Notified", ""].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
          <tbody>
            {(data.purchase_team_prs || []).length === 0 && <tr><td colSpan={7} className="py-8 text-center text-slate-500">Nothing waiting on the Purchase Team.</td></tr>}
            {(data.purchase_team_prs || []).map((pr) => (
              <tr key={pr.id} className="border-t border-slate-100">
                <td className={TD}><span className="mono font-semibold">{pr.code}</span><div><Pill value={pr.status} /></div></td>
                <td className={TD}>{pr.procurement_class === "regular" ? "Regular (exception)" : "Non-Regular"}</td>
                <td className={`${TD} text-xs`}>{(pr.lines || []).map((l, i) => <div key={i} className="mono">{fmtQty(l.qty)} {l.uom} · {(l.reason || "").replace("Auto MRP: ", "").slice(0, 60)}</div>)}</td>
                <td className={`${TD} text-xs`}>{pr.required_date || "—"}</td>
                <td className={`${TD} text-xs`}>{pr.routing_reason || "—"}</td>
                <td className={TD}><Pill value={pr.purchase_team_email_status} /></td>
                <td className={TD}>
                  {canAct && (
                    <Button size="sm" variant="outline" onClick={async () => {
                      try { await api.post(`/auto-procurement/purchase-requests/${pr.id}/mark-actioned`); toast.success(`${pr.code} marked actioned`); reload(); }
                      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                    }} data-testid={`ap-actioned-${pr.code}`}>Mark actioned</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {(data.unclassified || []).length > 0 && (
        <Section title={`Unclassified items below threshold (${data.unclassified.length}) — set Regular / Non-Regular in Items & BOM`}>
          <div className="px-4 py-3 text-xs text-slate-600 flex flex-wrap gap-2" data-testid="ap-unclassified">
            {data.unclassified.map((u) => <span key={u.item_id} className="mono bg-amber-50 text-amber-800 px-2 py-0.5 rounded">{u.item_code}</span>)}
          </div>
        </Section>
      )}

      <Section title={`Failed notifications (${data.failed_notifications?.length || 0})`}>
        <CommsTable rows={data.failed_notifications || []} />
      </Section>
    </div>
  );
}

function CommsTable({ rows }) {
  return (
    <table className="w-full text-sm" data-testid="ap-comms-table">
      <thead><tr>{["Date / time", "Channel", "Type", "PO / PR", "Supplier", "To", "Status", "Delivery", "Response / error"].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-500">No messages.</td></tr>}
        {rows.map((c) => (
          <tr key={c.id} className="border-t border-slate-100">
            <td className={`${TD} text-xs whitespace-nowrap`}>{fmtDT(c.sent_at)}</td>
            <td className={TD}>{c.channel === "whatsapp" ? <MessageCircle className="w-4 h-4 text-emerald-600" /> : <Mail className="w-4 h-4 text-blue-600" />}</td>
            <td className={`${TD} text-xs`}>{(c.kind || "").replace(/_/g, " ")}{c.followup_no ? ` #${c.followup_no}` : ""}</td>
            <td className={`${TD} mono text-xs`}>{c.po_code || c.pr_code || "—"}</td>
            <td className={`${TD} text-xs`}>{c.supplier || "—"}</td>
            <td className={`${TD} text-xs`}>{(c.to || []).join(", ") || "—"}</td>
            <td className={TD}><Pill value={c.status} /></td>
            <td className={TD}><Pill value={c.delivery_status} /></td>
            <td className={`${TD} text-xs max-w-xs`}>{c.response ? <span className="text-emerald-700">{c.response}{c.message ? ` — ${c.message}` : ""}</span> : c.error ? <span className="text-red-700">{c.error}</span> : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RunsTab() {
  const [runs, setRuns] = useState([]);
  useEffect(() => { api.get("/auto-procurement/runs?limit=100").then((r) => setRuns(r.data)).catch(() => {}); }, []);
  return (
    <Section title="MRP execution log">
      <table className="w-full text-sm" data-testid="ap-runs-table">
        <thead><tr>{["Started", "Finished", "Trigger", "Status", "Triggered", "PRs", "POs", "Sent", "Purchase Team", "Follow-ups", "Errors"].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
        <tbody>
          {runs.length === 0 && <tr><td colSpan={11} className="py-8 text-center text-slate-500">No MRP cycle has run yet.</td></tr>}
          {runs.map((r) => (
            <tr key={r.id} className="border-t border-slate-100">
              <td className={`${TD} text-xs whitespace-nowrap`}>{fmtDT(r.started_at)}</td>
              <td className={`${TD} text-xs whitespace-nowrap`}>{fmtDT(r.finished_at)}</td>
              <td className={`${TD} text-xs`}>{r.trigger}{r.actor && r.actor !== "auto-mrp" ? ` · ${r.actor}` : ""}</td>
              <td className={TD}><Pill value={r.status} /></td>
              <td className={`${TD} mono`}>{r.items_triggered}{r.unclassified_triggered ? <span className="text-amber-700"> (+{r.unclassified_triggered} unclassified)</span> : ""}</td>
              <td className={`${TD} mono text-xs`}>{(r.prs_created || []).join(", ") || "—"}</td>
              <td className={`${TD} mono text-xs`}>{(r.pos_created || []).join(", ") || "—"}</td>
              <td className={`${TD} mono`}>{r.pos_sent}</td>
              <td className={`${TD} mono text-xs`}>{(r.purchase_team_prs || []).join(", ") || "—"}</td>
              <td className={`${TD} text-xs`}>{r.followups ? `${r.followups.sent} sent · ${r.followups.stopped + r.followups.exhausted} closed` : "—"}</td>
              <td className={`${TD} text-xs text-red-700 max-w-xs`}>{(r.errors || []).join("; ") || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
}

function AuditTab() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const load = useCallback((query) => {
    api.get("/auto-procurement/audit", { params: { q: query || undefined, limit: 500 } }).then((r) => setRows(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(""); }, [load]);
  return (
    <Section title="Audit trail" right={
      <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); load(q); }}>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400" />
          <Input className="h-8 pl-7 w-56" placeholder="Item, PR, PO or supplier…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ap-audit-search" />
        </div>
        <Button size="sm" variant="outline" type="submit">Search</Button>
      </form>
    }>
      <table className="w-full text-sm" data-testid="ap-audit-table">
        <thead><tr>{["Date / time", "Event", "Actor", "Item", "PR", "PO", "Supplier", "Status", "Details"].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-500">No audit entries.</td></tr>}
          {rows.map((a) => (
            <tr key={a.id} className="border-t border-slate-100">
              <td className={`${TD} text-xs whitespace-nowrap`}>{fmtDT(a.at)}</td>
              <td className={`${TD} text-xs`}>{(a.event || "").replace(/_/g, " ")}</td>
              <td className={`${TD} text-xs`}>{a.actor}</td>
              <td className={`${TD} mono text-xs`}>{a.item_code || "—"}</td>
              <td className={`${TD} mono text-xs`}>{a.pr_code || "—"}</td>
              <td className={`${TD} mono text-xs`}>{a.po_code || "—"}</td>
              <td className={`${TD} text-xs`}>{a.supplier || "—"}</td>
              <td className={TD}><Pill value={a.status} /></td>
              <td className={`${TD} text-[11px] text-slate-500 max-w-md`}>
                {a.error && <div className="text-red-700">{a.error}</div>}
                {a.details && <AuditDetails d={a.details} qty={a.qty} />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
}

function AuditDetails({ d, qty }) {
  const bits = [];
  if (qty != null) bits.push(`Order qty ${fmtQty(qty)}`);
  if (d.stores) bits.push(`Stores: ${Object.entries(d.stores).map(([k, v]) => `${k}: ${fmtQty(v)}`).join(", ") || "none"}`);
  ["physical_stock", "reserved_qty", "available_qty", "reorder_level", "moq", "threshold", "pipeline_qty", "bom_demand_qty", "projected_qty", "net_requirement"].forEach((k) => {
    if (d[k] != null) bits.push(`${k.replace(/_/g, " ")}: ${fmtQty(d[k])}`);
  });
  ["email_status", "whatsapp_status", "email", "whatsapp", "reason", "ack_status", "remarks", "via", "count"].forEach((k) => {
    if (d[k] != null && d[k] !== "") bits.push(`${k.replace(/_/g, " ")}: ${d[k]}`);
  });
  if (d.recipients) bits.push(`to: ${d.recipients.join(", ") || "—"}`);
  if (d.items) bits.push(`items: ${Array.isArray(d.items) ? d.items.join(", ") : d.items}`);
  if (d.changes) bits.push(`changed: ${Object.keys(d.changes).join(", ") || "nothing"}`);
  return <div>{bits.join(" · ")}</div>;
}

function CommsTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/auto-procurement/communications?limit=500").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return <Section title="Supplier & Purchase Team communication log"><CommsTable rows={rows} /></Section>;
}

const PLACEHOLDERS = "{supplier_name} {po_no} {po_date} {delivery_date} {delivery_location} {payment_terms} {terms} {total} {company} {ack_link} {items_table} {followup_no} {pr_no}";

function SettingsTab({ isAdmin, onSaved }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/auto-procurement/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <div className="text-sm text-slate-500">Loading…</div>;
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const num = (k) => (e) => set(k, e.target.value === "" ? "" : Number(e.target.value));
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...s };
      ["whatsapp_token_configured", "whatsapp_webhook_configured", "email_provider_configured"].forEach((k) => delete payload[k]);
      const { data } = await api.put("/auto-procurement/settings", payload);
      setS((p) => ({ ...p, ...data }));
      toast.success("Settings saved");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };
  const Check = ({ k, label, hint }) => (
    <label className="flex items-start gap-2 text-sm cursor-pointer">
      <input type="checkbox" className="w-4 h-4 mt-0.5 accent-blue-600" checked={!!s[k]} disabled={!isAdmin} onChange={(e) => set(k, e.target.checked)} data-testid={`ap-set-${k}`} />
      <span>{label}{hint && <span className="block text-[11px] text-slate-500">{hint}</span>}</span>
    </label>
  );
  const Sel = ({ k, options }) => (
    <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-sm bg-white" value={s[k]} disabled={!isAdmin} onChange={(e) => set(k, e.target.value)} data-testid={`ap-set-${k}`}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
  const listField = (k) => (
    <Input value={(s[k] || []).join(", ")} disabled={!isAdmin} onChange={(e) => set(k, e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} data-testid={`ap-set-${k}`} />
  );
  const card = "bg-white border border-slate-200 rounded-lg p-5 space-y-4";
  return (
    <div className="space-y-5" data-testid="ap-settings">
      {!isAdmin && <div className="text-xs text-amber-700">Only an administrator can change these settings.</div>}
      <div className={card}>
        <div className="text-sm font-semibold text-slate-800">MRP scheduler</div>
        <Check k="enabled" label="Run the automated MRP every day" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label>Start time</Label><Input type="time" value={s.start_time} disabled={!isAdmin} onChange={(e) => set("start_time", e.target.value)} data-testid="ap-set-start_time" /></div>
          <div><Label>End time (23:59 = midnight)</Label><Input type="time" value={s.end_time} disabled={!isAdmin} onChange={(e) => set("end_time", e.target.value)} data-testid="ap-set-end_time" /></div>
          <div><Label>Frequency (minutes)</Label><Input type="number" min="5" value={s.frequency_minutes} disabled={!isAdmin} onChange={num("frequency_minutes")} data-testid="ap-set-frequency_minutes" /></div>
          <div><Label>Time zone</Label><Input value={s.timezone} disabled={!isAdmin} onChange={(e) => set("timezone", e.target.value)} /></div>
        </div>
      </div>

      <div className={card}>
        <div className="text-sm font-semibold text-slate-800">MOQ / reorder rules</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><Label>Trigger threshold</Label><Sel k="threshold_basis" options={[["reorder_level_or_moq", "Min Stock / reorder point, else MOQ"], ["reorder_level", "Min Stock / reorder point only"], ["moq", "MOQ only"]]} /></div>
          <div><Label>Stock basis</Label><Sel k="stock_basis" options={[["available", "Available (physical − reserved)"], ["physical", "Physical stock"]]} /></div>
          <div><Label>Trigger when projected stock is</Label><Sel k="trigger_condition" options={[["lte", "at or below threshold"], ["lt", "below threshold"]]} /></div>
          <div><Label>Default lead time (days)</Label><Input type="number" min="0" value={s.default_lead_time_days} disabled={!isAdmin} onChange={num("default_lead_time_days")} /></div>
          <div><Label>GST % on PO</Label><Input type="number" min="0" value={s.default_gst_pct} disabled={!isAdmin} onChange={num("default_gst_pct")} /></div>
          <div><Label>Unclassified items are treated as</Label><Sel k="unclassified_as" options={[["non_regular", "Non-Regular"], ["regular", "Regular"]]} /></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Check k="include_safety_stock" label="Add item safety stock to the threshold" />
          <Check k="include_open_pr" label="Count open PR quantity (prevents duplicate PRs)" />
          <Check k="include_open_po" label="Count open PO quantity (prevents duplicate POs)" />
          <Check k="include_in_transit" label="Count goods in transit (GIN awaiting inspection)" />
          <Check k="include_bom_demand" label="Add BOM demand from FG / SFG order shortfalls" />
          <Check k="include_manufactured_items" label="Also purchase items that have their own BOM" hint="Off: made items are exploded into component demand instead." />
          <Check k="include_unclassified" label="Procure items with no Regular / Non-Regular class" hint="Off: they are only listed on the dashboard." />
        </div>
      </div>

      <div className={card}>
        <div className="text-sm font-semibold text-slate-800">Supplier communication</div>
        <div className="text-[11px] text-slate-500">
          Email provider: {s.email_provider_configured ? "configured" : "not configured — emails are queued until RESEND_API_KEY is set"} ·
          WhatsApp: {s.whatsapp_provider === "webhook" ? (s.whatsapp_webhook_configured ? "gateway configured" : "WHATSAPP_WEBHOOK_URL not set") : (s.whatsapp_token_configured ? "Cloud API token configured" : "WHATSAPP_ACCESS_TOKEN not set")}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Check k="email_enabled" label="Email POs to suppliers" />
          <Check k="whatsapp_enabled" label="Send POs on WhatsApp" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><Label>WhatsApp provider</Label><Sel k="whatsapp_provider" options={[["meta_cloud", "WhatsApp Business Cloud API"], ["webhook", "Gateway webhook"]]} /></div>
          <div><Label>Phone number ID (Cloud API)</Label><Input value={s.whatsapp_phone_number_id} disabled={!isAdmin} onChange={(e) => set("whatsapp_phone_number_id", e.target.value)} /></div>
          <div><Label>API version</Label><Input value={s.whatsapp_api_version} disabled={!isAdmin} onChange={(e) => set("whatsapp_api_version", e.target.value)} /></div>
          <div><Label>Approved template name (optional)</Label><Input value={s.whatsapp_template_name} disabled={!isAdmin} onChange={(e) => set("whatsapp_template_name", e.target.value)} /></div>
          <div><Label>Template language</Label><Input value={s.whatsapp_template_lang} disabled={!isAdmin} onChange={(e) => set("whatsapp_template_lang", e.target.value)} /></div>
          <div><Label>Public URL for supplier confirm link</Label><Input value={s.public_base_url} placeholder="https://erp.example.com" disabled={!isAdmin} onChange={(e) => set("public_base_url", e.target.value)} /></div>
        </div>
        <div><Label>CC on supplier PO emails (comma separated)</Label>{listField("po_cc")}</div>
        <div><Label>PO terms &amp; conditions</Label><Textarea rows={4} value={s.po_terms} disabled={!isAdmin} onChange={(e) => set("po_terms", e.target.value)} /></div>
      </div>

      <div className={card}>
        <div className="text-sm font-semibold text-slate-800">Purchase Team (Non-Regular PRs)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Purchase Team emails (comma separated)</Label>{listField("purchase_team_emails")}</div>
          <div><Label>Also notify users with roles</Label>{listField("purchase_team_roles")}</div>
        </div>
      </div>

      <div className={card}>
        <div className="text-sm font-semibold text-slate-800">Supplier follow-ups</div>
        <Check k="followup_enabled" label="Send automatic follow-ups until the supplier confirms" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label>First follow-up after (hours)</Label><Input type="number" min="1" value={s.first_followup_hours} disabled={!isAdmin} onChange={num("first_followup_hours")} data-testid="ap-set-first_followup_hours" /></div>
          <div><Label>Then every (hours)</Label><Input type="number" min="1" value={s.repeat_followup_hours} disabled={!isAdmin} onChange={num("repeat_followup_hours")} /></div>
          <div><Label>Maximum follow-ups (0 = no limit)</Label><Input type="number" min="0" value={s.max_followups} disabled={!isAdmin} onChange={num("max_followups")} /></div>
          <div className="flex items-end"><Check k="followup_whatsapp" label="Also follow up on WhatsApp" /></div>
        </div>
      </div>

      <div className={card}>
        <div className="text-sm font-semibold text-slate-800">Templates</div>
        <div className="text-[11px] text-slate-500 mono">Placeholders: {PLACEHOLDERS}</div>
        <div><Label>PO email subject</Label><Input value={s.po_email_subject} disabled={!isAdmin} onChange={(e) => set("po_email_subject", e.target.value)} /></div>
        <div><Label>PO email body</Label><Textarea rows={8} value={s.po_email_body} disabled={!isAdmin} onChange={(e) => set("po_email_body", e.target.value)} /></div>
        <div><Label>Follow-up email subject</Label><Input value={s.followup_email_subject} disabled={!isAdmin} onChange={(e) => set("followup_email_subject", e.target.value)} /></div>
        <div><Label>Follow-up email body</Label><Textarea rows={6} value={s.followup_email_body} disabled={!isAdmin} onChange={(e) => set("followup_email_body", e.target.value)} /></div>
        <div><Label>WhatsApp PO message</Label><Textarea rows={3} value={s.whatsapp_po_message} disabled={!isAdmin} onChange={(e) => set("whatsapp_po_message", e.target.value)} /></div>
        <div><Label>WhatsApp follow-up message</Label><Textarea rows={3} value={s.whatsapp_followup_message} disabled={!isAdmin} onChange={(e) => set("whatsapp_followup_message", e.target.value)} /></div>
        <div><Label>Purchase Team email subject</Label><Input value={s.purchase_team_email_subject} disabled={!isAdmin} onChange={(e) => set("purchase_team_email_subject", e.target.value)} /></div>
      </div>

      {isAdmin && (
        <div className="flex justify-end">
          <Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="ap-settings-save">{busy ? "Saving…" : "Save settings"}</Button>
        </div>
      )}
    </div>
  );
}

export default function AutoProcurementPage() {
  const { user } = useAuth();
  const role = user?.role || "";
  const isAdmin = role === "admin";
  const canRun = ["admin", "planner", "purchase_manager", "production_manager"].includes(role);
  const canAct = ["admin", "planner", "purchase_manager", "purchase_executive"].includes(role);
  // ?tab=settings deep-links here from the Settings page.
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return TABS.some((x) => x.key === t) ? t : "dashboard";
  });
  const [dash, setDash] = useState(null);
  const [running, setRunning] = useState(false);
  const [tick, setTick] = useState(0);

  const loadDash = useCallback(async () => {
    try { const { data } = await api.get("/auto-procurement/dashboard"); setDash(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not load dashboard"); }
  }, []);
  useEffect(() => { loadDash(); }, [loadDash]);

  const runNow = async () => {
    if (!window.confirm("Run the MRP cycle now? Regular items below threshold will get PRs + POs sent to suppliers.")) return;
    setRunning(true);
    try {
      const { data } = await api.post("/auto-procurement/run");
      toast.success(`MRP ${data.status.replace(/_/g, " ")} — ${data.prs_created.length} PR(s), ${data.pos_created.length} PO(s), ${data.purchase_team_prs.length} to Purchase Team`);
      await loadDash();
      setTick((t) => t + 1);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "MRP run failed");
    } finally { setRunning(false); }
  };
  const runFollowups = async () => {
    try {
      const { data } = await api.post("/auto-procurement/followups/run");
      toast.success(`Follow-ups: ${data.sent} sent, ${data.stopped + data.exhausted} closed`);
      loadDash();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const sched = dash?.schedule;
  const schedLine = useMemo(() => {
    if (!sched) return "";
    if (!sched.enabled) return "Scheduler is OFF";
    return `${sched.in_window ? "Running window open" : "Outside daily window"} · next cycle ${sched.next_run_at ? new Date(sched.next_run_at).toLocaleString() : "—"} · last ${sched.last_run_at ? new Date(sched.last_run_at).toLocaleString() : "never"}`;
  }, [sched]);

  return (
    <div className="space-y-6" data-testid="auto-procurement-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Procurement</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Auto Procurement (MRP)
          </h1>
          <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5" data-testid="ap-schedule-line">
            <Clock className="w-3.5 h-3.5" /> {schedLine || "Loading schedule…"}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => { loadDash(); setTick((t) => t + 1); }} data-testid="ap-refresh"><RefreshCw className="w-4 h-4 mr-1.5" /> Refresh</Button>
          {canRun && <Button variant="outline" onClick={runFollowups} data-testid="ap-run-followups"><Mail className="w-4 h-4 mr-1.5" /> Send due follow-ups</Button>}
          {canRun && (
            <Button onClick={runNow} disabled={running} className="bg-blue-600 hover:bg-blue-700" data-testid="ap-run-now">
              <Play className="w-4 h-4 mr-1.5" /> {running ? "Running…" : "Run MRP now"}
            </Button>
          )}
        </div>
      </div>

      {dash?.counts?.failed_notifications > 0 && (
        <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2">
          <AlertTriangle className="w-4 h-4" /> {dash.counts.failed_notifications} supplier / Purchase Team notification(s) failed — see the Dashboard or Communications tab.
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap" data-testid="ap-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`ap-tab-${t.key}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${tab === t.key ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200 text-slate-700 hover:border-blue-300"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "dashboard" && <DashboardTab data={dash} reload={loadDash} canAct={canAct} />}
      {tab === "runs" && <RunsTab key={tick} />}
      {tab === "audit" && <AuditTab key={tick} />}
      {tab === "comms" && <CommsTab key={tick} />}
      {tab === "settings" && <SettingsTab isAdmin={isAdmin} onSaved={loadDash} />}
    </div>
  );
}
